<script setup lang="ts">
import {
	DropdownMenuRoot as DropdownMenu,
	TooltipRoot as Tooltip,
	TooltipProvider,
	TooltipTrigger,
} from "reka-ui";
import { computed } from "vue";
import {
	type RouteParams,
	type RouteRecord,
	type RouteRecordName,
	useRoute,
} from "vue-router";
import IconWorkspaces from "@/assets/icons/workspaces.svg";
import Breadcrumb from "@/components/ui/breadcrumb/Breadcrumb.vue";
import BreadcrumbEllipsis from "@/components/ui/breadcrumb/BreadcrumbEllipsis.vue";
import BreadcrumbItem from "@/components/ui/breadcrumb/BreadcrumbItem.vue";
import BreadcrumbLink from "@/components/ui/breadcrumb/BreadcrumbLink.vue";
import BreadcrumbList from "@/components/ui/breadcrumb/BreadcrumbList.vue";
import BreadcrumbPage from "@/components/ui/breadcrumb/BreadcrumbPage.vue";
import BreadcrumbSeparator from "@/components/ui/breadcrumb/BreadcrumbSeparator.vue";
import DropdownMenuContent from "@/components/ui/dropdown-menu/DropdownMenuContent.vue";
import DropdownMenuItem from "@/components/ui/dropdown-menu/DropdownMenuItem.vue";
import DropdownMenuTrigger from "@/components/ui/dropdown-menu/DropdownMenuTrigger.vue";
import TooltipContent from "@/components/ui/tooltip/TooltipContent.vue";

const route = useRoute();

interface BreadcrumbOutputItem {
	path: string;
	name?: RouteRecordName;
}

function replaceDynamicPathSegments(path: string) {
	const params = route.params as RouteParams;
	let p = path;
	for (const [key, value] of Object.entries(params)) {
		p = p.replace(`:${key}`, value as string);
	}
	return p;
}

const breadcrumbs = computed<BreadcrumbOutputItem[]>(() => {
	const breadcrumbItems: BreadcrumbOutputItem[] = [];

	for (const match of route.matched) {
		const formattedPath = replaceDynamicPathSegments(match.path);

		// Skip path if it's a duplicate route (e.g. 'Project' and 'Project Overview' routes)
		if (breadcrumbItems.some((crumb) => crumb.path === formattedPath)) {
			continue;
		}

		const itemName = determineItemName(match, route.params);
		breadcrumbItems.push({
			name: itemName,
			path: formattedPath,
		});
	}

	return breadcrumbItems;
});

function determineItemName(match: RouteRecord, params: RouteParams): string {
	const breadcrumbName = match.meta?.breadcrumbName as string;
	let itemName: RouteRecordName = match.name as RouteRecordName;

	if (itemName === "Product") {
		itemName = params.productName.toString() || "NA";
	} else if (itemName === "Project") {
		itemName = params.projectName.toString() || "NA";
	} else if (itemName === "Workspace") {
		itemName = params.workspaceName.toString();
	} else if (isValidBreadcrumbName(breadcrumbName)) {
		itemName = breadcrumbName;
	}

	return itemName as string;
}

function isValidBreadcrumbName(
	breadcrumbName: string,
): breadcrumbName is string {
	return typeof breadcrumbName === "string" && breadcrumbName.length > 0;
}

const displayedBreadcrumbCount = 2;
const displayedBreadcrumbs = computed(() => {
	if (breadcrumbs.value.length <= displayedBreadcrumbCount + 1) {
		return breadcrumbs.value;
	}
	return breadcrumbs.value.filter(
		(_, i) => i >= breadcrumbs.value.length - displayedBreadcrumbCount,
	);
});

const dropdownBreadcrumbs = computed(() => {
	if (breadcrumbs.value.length <= displayedBreadcrumbCount + 1) {
		return [];
	}
	return breadcrumbs.value.filter(
		(_, i) => i < breadcrumbs.value.length - displayedBreadcrumbCount,
	);
});
</script>

<template>
	<Breadcrumb class="pr-8">
		<BreadcrumbList>
			<TooltipProvider>
				<BreadcrumbItem>
					<Tooltip>
						<TooltipTrigger as-child>
							<BreadcrumbLink href="/"
								><IconWorkspaces class="w-5 h-5"
							/></BreadcrumbLink>
						</TooltipTrigger>
						<TooltipContent>Workspaces</TooltipContent>
					</Tooltip>
				</BreadcrumbItem>
			</TooltipProvider>
			<BreadcrumbSeparator />
			<template v-if="dropdownBreadcrumbs.length > 0">
				<DropdownMenu>
					<DropdownMenuTrigger class="flex items-center gap-1">
						<BreadcrumbEllipsis class="h-4 w-4" />
						<span class="sr-only">Toggle menu</span>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem
							v-for="crumb in dropdownBreadcrumbs"
							:key="crumb.path"
							class="p-0"
						>
							<BreadcrumbLink :href="crumb.path" class="w-full px-2 py-1.5">
								{{ crumb.name }}
							</BreadcrumbLink>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<BreadcrumbSeparator />
			</template>
			<template v-for="(crumb, i) in displayedBreadcrumbs" :key="i">
				<BreadcrumbItem>
					<BreadcrumbLink
						:href="crumb.path"
						v-if="i < displayedBreadcrumbs.length - 1"
					>
						{{ crumb.name }}
					</BreadcrumbLink>
					<BreadcrumbPage v-else>
						{{ crumb.name }}
					</BreadcrumbPage>
				</BreadcrumbItem>
				<BreadcrumbSeparator v-if="i < displayedBreadcrumbs.length - 1" />
			</template>
		</BreadcrumbList>
	</Breadcrumb>
</template>

<style scoped></style>
